import React, { useState, useRef, useEffect } from "react";
import {
  List,
  Box,
  Button,
  Typography,
  Divider,
  Card,
  Grid,
  Stack,
  FormControlLabel,
} from "@mui/material";

import { useResponsive } from "../../../hooks/useResponsive";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCard,
  fetchPaymentToken,
  makePayment,
} from "../../../redux/payments/action";
import { cancelReservationRequest } from "../../../redux/reservations/action";

import moment from "moment-timezone";
import { MOMENT_CONSTANTS } from "../../../utils/momentUtils";
import {
  getPaymentType,
  initializeBraintree,
  PAYMENT_TYPE,
} from "../../payments/makePayment/utils";
import storePersist from "../../../services/localStorage";
import { localConstant } from "../../../services/localStorage";

import MenuBar from "../../menuBar";
import NavBar from "../../../NavBar";
import CustomAlert from "../../../components/CustomAlert";
import CustomLoader from "../../../components/CustomLoader";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CancelReservationModal from "../../../components/reservation/Modals/CancelReservationModal";
import { useTranslation } from "react-i18next";

import { enqueueSnackbar } from "notistack";
import SnackbarConfig from "../../../utils/snackbarConfig";
import ConfirmationDialog from "../../../components/ConfirmationDialog";

const braintreeContainerId = "dropin-invoice-container";

const secondaryStyles = {
  backgroundColor: "#fff",
  borderColor: "#8A8886",
  color: "#605E5C",
  ":hover": { backgroundColor: "transparent" },
};

const hidePaylater = true;

const InvoicePayment = () => {
  const lgUp = useResponsive("up", "lg");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const [deleteCardDialog, setDeleteCardDialog] = useState({
    isOpen: false,
    title: `${t("pages.deleteCard.key01")} ?`,
    content: `${t("pages.deleteCard.key02")}`,
    reset: () => setDeleteCardDialog((prev) => ({ ...prev, isOpen: false })),
    handleContinue: undefined,
    isLoadingDailog: false,
  });
  const goHome = () => navigate("/ownerDashboard");

  const { paymentToken, isLoading } = useSelector(
    (state) => state.paymentsReducer,
  );
  const { state } = location;

  const customerId = storePersist.get(localConstant.ENTITY_CUSTOMER_ID);

  const [checked, setChecked] = useState({
    autoPayEnabled: false,
    savePaymentMethod: true,
  });

  const handleSavePaymentClick = () => {
    setChecked((prev) => ({
      ...prev,
      savePaymentMethod: !prev.savePaymentMethod,
    }));
  };

  const myRef = useRef({});
  const [isInitialising, setInitialising] = useState(false);
  const [loading, setLoading] = useState(false);
  const [instance, setInstance] = useState(null);
  const [billing, setBilling] = useState({ totalAmount: 0, billNums: [] });
  const [dialogProps, setDialogProps] = useState({
    step: 0,
    isOpen: false,
    reason: "",
    isReservation: false,
    isPayment: true,
    onCancel: () =>
      setDialogProps((prev) => ({
        ...prev,
        isOpen: false,
        step: 0,
        isReservation: false,
        isPayment: true,
      })),
  });
  const [alertProps, setAlertProps] = useState({
    isOpen: false,
    title: "",
    content: "",
    onClick: () => {
      setAlertProps((prev) => ({ ...prev, isOpen: false }));
      goHome();
    },
  });

  useEffect(() => {
    dispatch(fetchPaymentToken(state?.customerId || customerId));
  }, []);

  useEffect(() => {
    if (!state?.billing) {
      enqueueSnackbar("Failed to fetch Bill Amount", {
        variant: "error",
        autoHideDuration: 3000,
        action: SnackbarConfig,
      });
      return;
    }
    const { charges = [] } = state?.billing || {};
    const formattedCharges = charges.reduce(
      (acc, val) => {
        acc.totalAmount = (acc.totalAmount || 0) + val.totalAmount;
        acc.billNums = [...(acc.billNums || []), val.billNums];
        return acc;
      },
      { totalAmount: 0, billNums: [] },
    );

    setBilling(formattedCharges);
  }, [state]);

  useEffect(() => {
    if (billing.totalAmount && paymentToken) {
      initializeBraintree(paymentToken, `#${braintreeContainerId}`, {
        onStart: () => setInitialising(true),
        onSuccess: (instance) => {
          setInstance(instance);
        },
        onDone: () => setInitialising(false),
      });
    }
  }, [paymentToken]);

  const getPaymentAlertProps = (condition, errorMessage) => {
    const title = `${t("pages.paymentAlertPopUp.text01")} ${
      condition
        ? t("pages.paymentAlertPopUp.text02")
        : t("pages.paymentAlertPopUp.text03")
    }`;
    const variant = condition ? "success" : "error";
    const content = condition
      ? t("pages.paymentAlertPopUp.text05")
      : errorMessage;
    return { title, variant, content };
  };

  const getDeleteCardAlertProps = (condition, errorMessage) => {
    const title = `${t("pages.deleteCardAlertPopUp.text01")} ${
      condition
        ? t("pages.deleteCardAlertPopUp.text02")
        : t("pages.deleteCardAlertPopUp.text03")
    }`;
    const subTitle = "";
    const variant = condition ? "success" : "error";
    const content = condition
      ? t("pages.deleteCardAlertPopUp.text05")
      : errorMessage;
    return { title, subTitle, variant, content };
  };

  const getCancelAlertProps = (response) => {
    const condition = response.success ? true : false;
    const title = condition
      ? !state.isExtend
        ? t("pages.paymentCancelPopUp.title01")
        : t("pages.paymentCancelPopUp.title02")
      : t("pages.paymentCancelPopUp.title03");
    const variant = condition ? "success" : "error";
    const content = condition
      ? `${t("pages.paymentCancelPopUp.text02")} ${moment()?.format(
          MOMENT_CONSTANTS.date.fullMonthDay,
        )}`
      : response.error;

    return { title, variant, content };
  };

  const getPaymentProps = () => {
    return {
      savePaymentMethod: checked.savePaymentMethod,
      autoPayEnabled: checked.autoPayEnabled,
    };
  };

  const dispatchPayment = (payload) =>
    dispatch(
      makePayment(
        {
          customerId: state?.customerId || customerId,
          reservationId: null,
          ...getPaymentProps(),
        },
        {
          requestType: "CREATE_PAYMENT",
          paymentCurrencyCd: "USD",
          payments: [
            {
              paymentModeCd: getPaymentType(payload?.type) || "CRDCARD",
              paymentAmount: billing.totalAmount?.toFixed(2),
              paymentAttributes: {
                nonce: payload.nonce,
                paymentGatewayCorrelationId: JSON.parse(payload.deviceData)
                  ?.correlation_id,
              },
              criteria: {
                billNums: billing.billNums,
              },
            },
          ],
        },
        (response, errorMessage = "") => {
          setAlertProps((prev) => ({
            ...prev,
            isOpen: true,
            ...getPaymentAlertProps(response, errorMessage),
          }));
        },
        true,
      ),
    );

  const dispatchDeleteCard = (payload) =>
    dispatch(
      deleteCard(
        {
          customerId: state?.customerId || customerId,
        },
        { nonce: payload.nonce.toString() },
        (response, errorMessage = "") => {
          setAlertProps((prev) => ({
            ...prev,
            isOpen: true,
            ...getDeleteCardAlertProps(response, errorMessage),
          }));
        },
      ),
    );

  const handlePayment = () => {
    if (instance) {
      instance.requestPaymentMethod(function (err, payload) {
        if (err) {
          console.error("Error requesting payment method:", err);
        } else {
          console.log({ payload });
          dispatchPayment(payload);
        }
      });
    }
  };

  const submitDialog = () => {
    setDeleteCardDialog((props) => ({
      ...props,
      isOpen: true,
      handleContinue: () => {
        handleDeleteCard();
      },
    }));
  };

  const handleDeleteCard = () => {
    if (instance) {
      instance.requestPaymentMethod(function (err, payload) {
        if (err) {
          console.error("Error requesting payment method:", err);
        } else {
          console.log({ payload });
          dispatchDeleteCard(payload);
        }
      });
    }
  };

  const handleCancelReservation = () => {
    setDialogProps({
      step: 1,
      isOpen: true,
      isReservation: false,
      isPayment: true,
      onCancel: () =>
        setDialogProps((prev) => ({
          ...prev,
          isOpen: false,
          step: 0,
          isReservation: false,
          isPayment: true,
        })),
      onSubmit: () => navigate("/billing_payments"),
    });
  };

  return (
    <div
      style={{
        width: lgUp ? `calc(100% - ${240}px)` : "100%",
        marginLeft: lgUp ? "240px" : 0,
      }}
    >
      <NavBar
        myRef={myRef}
        enableBack={false}
        title={t("pages.makePayment.pageHeading")}
      />
      <MenuBar myRef={myRef} />
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <List
          sx={{
            bgcolor: "background.paper",
            width: "100%",
            overflow: "auto",
            height: "100%",
            minHeight: "85vh",
          }}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            margin={"20px 20px"}
          >
            <Typography
              variant="h5"
              color={"primary.secondary"}
              textTransform={"capitalize"}
            >
              {t("pages.makePayment.text01")} $
              {billing?.totalAmount?.toFixed(2) || 0}
            </Typography>
          </Box>
          <Divider />
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            margin={"20px 20px"}
          >
            <Box width={"85vw"} maxWidth={550}>
              <div id={braintreeContainerId} />
              {instance && (
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={0}
                >
                  <Typography
                    onClick={() => submitDialog()}
                    sx={{
                      fontSize: 17,
                      textAlign: "right",
                      color: "red",
                      textDecoration: "underline",
                      cursor: "pointer",
                      "&:hover": {
                        color: "darkblue",
                      },
                    }}
                  >
                    {t("pages.makePayment.text08")}
                  </Typography>
                </Stack>
              )}
              {(isInitialising || !instance) && (
                <Card
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {(isLoading || isInitialising) && !instance ? (
                    <Typography sx={{ padding: "10px 0" }}>
                      {t("pages.makePayment.text02")}
                    </Typography>
                  ) : (
                    !instance && (
                      <Typography sx={{ padding: "10px 0", color: "red" }}>
                        {t("pages.makePayment.text03")}
                      </Typography>
                    )
                  )}
                </Card>
              )}
            </Box>
          </Box>
        </List>

        <Box sx={{ mt: 5 }}>
          <Grid
            container
            spacing={1}
            width={"80%"}
            maxWidth={550}
            sx={{
              margin: "auto",
              left: 0,
              right: 0,
              position: "absolute",
              bottom: 10,
            }}
          >
            {!state?.hideCancel && (
              <Grid item xs={6}>
                <Button
                  fullWidth
                  sx={secondaryStyles}
                  onClick={handleCancelReservation}
                  variant="contained"
                  size="large"
                >
                  {t("commonLabels.text02")}
                </Button>
              </Grid>
            )}
            {!hidePaylater && (
              <Grid item xs={6}>
                <Button
                  fullWidth
                  onClick={goHome}
                  variant="contained"
                  size="large"
                  sx={state?.hideCancel && secondaryStyles}
                >
                  {t("pages.makePayment.text04")}
                </Button>
              </Grid>
            )}

            <Grid
              item
              xs={
                state?.hideCancel
                  ? hidePaylater
                    ? 12
                    : 6
                  : hidePaylater
                    ? 6
                    : 12
              }
            >
              {instance && (
                <Button
                  sx={{
                    width: "100%",
                  }}
                  disabled={isLoading}
                  onClick={handlePayment}
                  variant="contained"
                  size="large"
                >
                  {t("pages.makePayment.text05")}
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </div>
      {dialogProps.step === 1 && <CancelReservationModal {...dialogProps} />}
      {alertProps.isOpen && <CustomAlert {...alertProps} />}
      {deleteCardDialog.isOpen && <ConfirmationDialog {...deleteCardDialog} />}
      {(isLoading || loading) && <CustomLoader />}
    </div>
  );
};

export default InvoicePayment;
